// /app/frontend/src/components/frugal-v2/services/frugalApi.js
// API service for Frugal v2 (120 lines)
import { toast } from 'sonner';

const API_BASE = `${process.env.REACT_APP_BACKEND_URL}/api/v2/frugal`;

const getUserTimezone = () => {
  try {
    return Intl.DateTimeFormat().resolvedOptions().timeZone || 'UTC';
  } catch {
    return 'UTC';
  }
};

const getHeaders = (token) => ({
  'Content-Type': 'application/json',
  'Authorization': `Bearer ${token}`,
  'X-User-Timezone': getUserTimezone()
});

let lastExpiredToastAt = 0;
const showExpiredToast = () => {
  const now = Date.now();
  if (now - lastExpiredToastAt < 4000) return;
  lastExpiredToastAt = now;
  toast.error('Tu trial finalizo. Activa tu plan para realizar cambios.', {
    duration: 8000,
    action: {
      label: 'Activar Plan',
      onClick: async () => {
        try {
          const token = localStorage.getItem('token');
          const res = await fetch(`${API_BASE}/subscription/payment-link?plan=yearly`, {
            headers: { 'Authorization': `Bearer ${token}` }
          });
          const data = await res.json();
          if (data.url) {
            window.location.href = data.url;
          } else {
            toast.error('No se pudo abrir el link de pago. Intenta desde el menu Mi Suscripcion.');
          }
        } catch {
          toast.error('Error de conexion');
        }
      }
    }
  });
};

// Generic fetch wrapper
export const apiFetch = async (endpoint, options = {}, token) => {
  const response = await fetch(`${API_BASE}${endpoint}`, {
    ...options,
    headers: getHeaders(token)
  });
  
  if (!response.ok) {
    const error = await response.json().catch(() => ({ detail: 'Network error' }));
    if (response.status === 403 && error.detail === 'SUBSCRIPTION_EXPIRED') {
      showExpiredToast();
    }
    const err = new Error(error.detail || 'Request failed');
    err.detail = error.detail;
    err.status = response.status;
    throw err;
  }
  
  return response.json();
};

// Data endpoints
export const fetchFrugalData = (token) => apiFetch('/data', {}, token);
export const fetchZenSummary = (token) => apiFetch('/zen', {}, token);

// Incomes
export const fetchIncomes = (token) => apiFetch('/incomes', {}, token);
export const createIncome = (token, data) => apiFetch('/incomes', { method: 'POST', body: JSON.stringify(data) }, token);
export const updateIncome = (token, id, data) => apiFetch(`/incomes/${id}`, { method: 'PUT', body: JSON.stringify(data) }, token);
export const deleteIncome = (token, id) => apiFetch(`/incomes/${id}`, { method: 'DELETE' }, token);
export const toggleIncomeStatus = (token, id, body = {}) => apiFetch(`/incomes/${id}/toggle-status`, { method: 'PUT', body: JSON.stringify(body) }, token);

// Savings
export const fetchSavings = (token) => apiFetch('/savings', {}, token);
export const createSaving = (token, data) => apiFetch('/savings', { method: 'POST', body: JSON.stringify(data) }, token);
export const updateSaving = (token, id, data) => apiFetch(`/savings/${id}`, { method: 'PUT', body: JSON.stringify(data) }, token);
export const deleteSaving = (token, id) => apiFetch(`/savings/${id}`, { method: 'DELETE' }, token);
export const toggleSavingPaid = (token, id) => apiFetch(`/savings/${id}/toggle-paid`, { method: 'PUT' }, token);

// Expenses
export const fetchExpenses = (token) => apiFetch('/expenses', {}, token);
export const createExpense = (token, data) => apiFetch('/expenses', { method: 'POST', body: JSON.stringify(data) }, token);
export const updateExpense = (token, id, data) => apiFetch(`/expenses/${id}`, { method: 'PUT', body: JSON.stringify(data) }, token);
export const deleteExpense = (token, id) => apiFetch(`/expenses/${id}`, { method: 'DELETE' }, token);
export const toggleExpensePaid = (token, id, accountId = null) => {
  const url = accountId ? `/expenses/${id}/toggle-paid?account_id=${accountId}` : `/expenses/${id}/toggle-paid`;
  return apiFetch(url, { method: 'PUT' }, token);
};

// Budget
export const fetchBudget = (token) => apiFetch('/budget', {}, token);
export const saveBudget = (token, data) => apiFetch('/budget', { method: 'PUT', body: JSON.stringify(data) }, token);
export const fetchBudgetExecution = (token) => apiFetch('/budget/execution', {}, token);
export const fetchExpenseCategories = (token) => apiFetch('/budget/categories/expenses', {}, token);
export const fetchCategoryTransactions = (token, category) => apiFetch(`/budget/execution/${encodeURIComponent(category)}/transactions`, {}, token);
export const fetchIncomeCategories = (token) => apiFetch('/budget/categories/incomes', {}, token);
export const fetchSavingCategories = (token) => apiFetch('/budget/categories/savings', {}, token);

// Reserves & Contingency
export const fetchReserves = (token) => apiFetch('/reserves', {}, token);
export const fetchReserveExecution = (token) => apiFetch('/reserves/execution', {}, token);
export const fetchContingency = (token) => apiFetch('/contingency', {}, token);
export const fetchContingencyRecords = (token) => apiFetch('/contingency/records', {}, token);

// Alerts
export const fetchUpcomingPayments = (token) => apiFetch('/alerts/upcoming-payments', {}, token);
export const fetchAlertsCount = (token) => apiFetch('/alerts/count', {}, token);

// Emails
export const fetchPendingEmails = (token) => apiFetch('/emails/pending', {}, token);
export const fetchCurrentMonthEmails = (token) => apiFetch('/emails/current-month', {}, token);
export const fetchEmailDetail = (token, id) => apiFetch(`/emails/${id}`, {}, token);
export const fetchEmailSettings = (token) => apiFetch('/emails/settings', {}, token);
export const approveEmail = (token, id, data) => apiFetch(`/emails/${id}/approve`, { method: 'POST', body: JSON.stringify(data) }, token);
export const rejectEmail = (token, id) => apiFetch(`/emails/${id}/reject`, { method: 'POST' }, token);

// Closing
export const fetchClosingStatus = (token) => apiFetch('/closing/status', {}, token);
export const fetchClosingSummary = (token) => apiFetch('/closing/summary', {}, token);
export const executeClosing = (token, confirmUnpaid) => apiFetch('/closing/execute', { method: 'POST', body: JSON.stringify({ confirm_unpaid: confirmUnpaid }) }, token);
export const fetchClosingHistory = (token) => apiFetch('/closing/history', {}, token);

// Funds and Alerts
export const fetchFundStatus = (token) => apiFetch('/funds/status', {}, token);
export const fetchFundAlerts = (token) => apiFetch('/funds/alerts', {}, token);
export const fetchFundAlertsCount = (token) => apiFetch('/funds/alerts/count', {}, token);
export const markAlertsRead = (token, alertIds = null) => apiFetch('/funds/alerts/mark-read', { method: 'POST', body: JSON.stringify({ alert_ids: alertIds }) }, token);

// Banks/Cash Balance
export const fetchBankBalance = (token) => apiFetch('/banks/balance', {}, token);
export const updateBankBalance = (token, balance) => apiFetch('/banks/balance', { method: 'PUT', body: JSON.stringify({ balance }) }, token);
export const toggleBankVisibility = (token) => apiFetch('/banks/toggle-visibility', { method: 'POST' }, token);

// Accounts (Banks and Cards)
export const fetchAccounts = (token) => apiFetch('/accounts', {}, token);
export const createAccount = (token, data) => apiFetch('/accounts', { method: 'POST', body: JSON.stringify(data) }, token);
export const updateAccount = (token, id, data) => apiFetch(`/accounts/${id}`, { method: 'PUT', body: JSON.stringify(data) }, token);
export const deleteAccount = (token, id) => apiFetch(`/accounts/${id}`, { method: 'DELETE' }, token);
export const setDefaultAccount = (token, id) => apiFetch(`/accounts/${id}`, { method: 'PUT', body: JSON.stringify({ is_default: true }) }, token);
export const fetchAccountTransactions = (token, id) => apiFetch(`/accounts/${id}/transactions`, {}, token);
export const reconcileTransaction = (token, data) => apiFetch(`/accounts/transactions/reconcile`, { method: 'POST', body: JSON.stringify(data) }, token);

// Chat (Maestro ZEN) - Now accepts financial context
export const sendChatMessage = (token, message, language = 'es', context = null) => 
  apiFetch('/chat', { method: 'POST', body: JSON.stringify({ message, language, context }) }, token);
export const getChatHistory = (token, limit = 20) => 
  apiFetch(`/chat/history?limit=${limit}`, {}, token);

// Receipt OCR
export const analyzeReceipt = (token, imageBase64) => 
  apiFetch('/receipts/analyze', { method: 'POST', body: JSON.stringify({ image_base64: imageBase64 }) }, token);

export default {
  fetchFrugalData,
  fetchZenSummary,
  fetchIncomes, createIncome, updateIncome, deleteIncome, toggleIncomeStatus,
  fetchSavings, createSaving, updateSaving, deleteSaving, toggleSavingPaid,
  fetchExpenses, createExpense, updateExpense, deleteExpense, toggleExpensePaid,
  fetchBudget, saveBudget, fetchBudgetExecution,
  fetchExpenseCategories, fetchIncomeCategories, fetchSavingCategories,
  fetchReserves, fetchReserveExecution, fetchContingency, fetchContingencyRecords,
  fetchUpcomingPayments, fetchAlertsCount,
  fetchPendingEmails, fetchEmailDetail, fetchEmailSettings, approveEmail, rejectEmail,
  fetchClosingStatus, fetchClosingSummary, executeClosing,
  fetchFundStatus, fetchFundAlerts, fetchFundAlertsCount, markAlertsRead,
  fetchBankBalance, updateBankBalance, toggleBankVisibility,
  fetchAccounts, createAccount, updateAccount, deleteAccount, setDefaultAccount,
  sendChatMessage, getChatHistory
};
